import {
    HttpErrorResponse,
    HttpInterceptorFn,
    HttpResponse,
    HttpHeaders
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { CasdoorAuthService } from 'app/core/auth/casdoor-auth.service';
import { AuthAlertService } from 'app/core/auth/auth-alert.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { catchError, from, map, switchMap, throwError } from 'rxjs';

// CapacitorHttp only accepts string/JSON bodies on native platforms (per Capacitor's own
// docs) - passing a FormData instance directly loses its multipart structure and the
// backend rejects it with 415. Convert it into the array shape Capacitor's native layer
// understands via `dataType: 'formData'` (see CapacitorUrlRequest.swift / CapacitorHttpUrlConnection.java):
// each binary part is base64-encoded here and decoded back to raw bytes natively, so the
// Capacitor JS bridge (which isn't byte-safe for raw binary strings/JSON) never touches
// the actual file bytes.
async function blobToBase64(blob: Blob): Promise<string> {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}

async function formDataToNativeParts(formData: FormData): Promise<{ parts: any[]; contentType: string }> {
    const boundary = `----DipitaBoundary${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
    const parts: any[] = [];

    // tsconfig's `lib` doesn't include `dom.iterable`, so FormData.entries() isn't typed
    for (const [key, value] of (formData as any).entries() as IterableIterator<[string, FormDataEntryValue]>) {
        if (value instanceof Blob) {
            const filename = value instanceof File ? value.name : 'blob';
            const contentType = value.type || 'application/octet-stream';
            parts.push({
                type: 'base64File',
                key,
                value: await blobToBase64(value),
                fileName: filename,
                contentType
            });
        } else {
            parts.push({ type: 'string', key, value });
        }
    }

    return { parts, contentType: `multipart/form-data; boundary=${boundary}` };
}

function base64ToBlob(base64: string, contentType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}

function hasStoredSession(): boolean {
    return !!(
        localStorage.getItem('refresh_token') ||
        sessionStorage.getItem('refresh_token') ||
        localStorage.getItem('access_token') ||
        sessionStorage.getItem('access_token')
    );
}

function clearStoredTokens(): void {
    for (const key of ['access_token', 'refresh_token', 'expires_at', 'accessToken', 'refreshToken']) {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    }
}

function shouldRedirectToSignIn(router: Router, authService: CasdoorAuthService): boolean {
    return !router.url.includes('sign-in') && !authService.isAuthLoading() && !authService.isAuthenticated();
}

function isTokenExpirationError(error: HttpErrorResponse): boolean {
    if (!error) return false;

    const errorMessage = error.message || error.error?.error_description || '';
    const errorString = errorMessage.toLowerCase();

    return (
        errorString.includes('refresh token') ||
        errorString.includes('invalid refresh token') ||
        errorString.includes('unknown or invalid refresh token') ||
        errorString.includes('expired') ||
        errorString.includes('token expired') ||
        errorString.includes('forbidden')
    );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(CasdoorAuthService);
    const authAlertService = inject(AuthAlertService);
    const router = inject(Router);

    if (req.url.indexOf('aplt_backend/api') >= 0) {
        return from(authService.getAccessTokenSilently()).pipe(
            map((accessToken) => {
                if (!accessToken || AuthUtils.isTokenExpired(accessToken)) {
                    throw new Error('No access token available');
                }

                const sanitizedBase = environment.backendUrl.replace(/\/+$/,'');
                const sanitizedPath = req.url.replace("aplt_backend/", "").replace(/^\/+/, '');

                // For FormData requests, don't set Content-Type manually
                // Let the browser/iOS handle it automatically to ensure proper multipart/form-data with boundary
                const isFormData = req.body instanceof FormData;
                let headers = req.headers;

                // Only add Authorization header, preserve existing headers
                // For FormData, Angular/browser will automatically set Content-Type with boundary
                if (isFormData) {
                    // Remove any manually set Content-Type to let browser handle it
                    if (headers.has('Content-Type')) {
                        headers = headers.delete('Content-Type');
                    }
                    headers = headers.set('Authorization', 'Bearer ' + accessToken);
                } else {
                    headers = headers.set('Authorization', 'Bearer ' + accessToken);
                }

                return req.clone({
                    url: `${sanitizedBase}/${sanitizedPath}`,
                    headers: headers,
                });
            }),
            switchMap((updatedReq) => {
                // On native platforms, bypass Angular's XMLHttpRequest (which gets intercepted by CapacitorHttp JS patch and fails CORS)
                // Instead, call CapacitorHttp.request() directly, which doesn't send the Origin header.
                if (Capacitor.isNativePlatform() && updatedReq.url.includes('api.dipitatouch.com')) {
                    const headersObj: Record<string, string> = {};
                    updatedReq.headers.keys().forEach(k => {
                        const val = updatedReq.headers.get(k);
                        if (val !== null) headersObj[k] = val;
                    });

                    const isBlobRequest = updatedReq.responseType === 'blob';

                    return from((async () => {
                        // For CapacitorHttp, body must be appropriately stringified if it's JSON
                        let data: any = updatedReq.body;
                        let dataType: 'formData' | undefined;
                        if (data instanceof FormData) {
                            const multipart = await formDataToNativeParts(data);
                            data = multipart.parts;
                            headersObj['Content-Type'] = multipart.contentType;
                            dataType = 'formData';
                        } else if (data && typeof data === 'object') {
                            headersObj['Content-Type'] = 'application/json';
                            data = JSON.stringify(data);
                        }

                        return CapacitorHttp.request({
                            method: updatedReq.method,
                            url: updatedReq.urlWithParams,
                            headers: headersObj,
                            data: data,
                            dataType,
                            // Binary responses (file downloads) come back as base64 - without this,
                            // CapacitorHttp/JSON-parses them as text, corrupting the file content.
                            responseType: isBlobRequest ? 'blob' : 'json'
                        });
                    })()).pipe(
                        map(response => {
                            let parsedBody: any = response.data;

                            if (isBlobRequest && response.status < 400) {
                                const contentType = response.headers?.['content-type'] || response.headers?.['Content-Type'] || 'application/octet-stream';
                                parsedBody = base64ToBlob(parsedBody, contentType);
                            } else if (typeof parsedBody === 'string') {
                                try { parsedBody = JSON.parse(parsedBody); } catch (e) {}
                            }

                            if (response.status >= 400) {
                                throw new HttpErrorResponse({
                                    error: parsedBody,
                                    status: response.status,
                                    url: response.url,
                                    statusText: 'Error'
                                });
                            }
                            return new HttpResponse({
                                body: parsedBody,
                                status: response.status,
                                headers: new HttpHeaders(response.headers),
                                url: response.url
                            });
                        })
                    );
                }
                return next(updatedReq);
            }),
            catchError((error: any) => {
                // Ensure raw Capacitor errors or JS errors from the native call are wrapped in HttpErrorResponse
                if (!(error instanceof HttpErrorResponse) && error?.message && !error.message.includes('refresh token') && !error.message.includes('access token')) {
                    error = new HttpErrorResponse({
                        error: error.message || error,
                        status: 0,
                        statusText: 'Unknown Native Error',
                        url: req.url
                    });
                }

                // Handle Casdoor authentication errors (not HttpErrorResponse)
                if (error?.error === 'missing_refresh_token' || 
                    error?.error_description?.includes('Missing Refresh Token') ||
                    error?.error === 'invalid_token' ||
                    error?.message?.includes('No access token available') ||
                    error?.message?.includes('No refresh token available') ||
                    error?.message?.includes('Refresh failed')) {
                    // User is not authenticated or session expired
                    const hadSession = hasStoredSession();
                    if (shouldRedirectToSignIn(router, authService)) {
                        console.warn('Missing refresh token - redirecting to sign-in');
                        clearStoredTokens();
                        if (hadSession) {
                            authAlertService.showSessionExpiredAlert();
                        }
                        router.navigate(['/sign-in']);
                    }
                    // Return a proper error response
                    return throwError(() => new HttpErrorResponse({
                        error: error,
                        status: 401,
                        statusText: 'Unauthorized'
                    }));
                }

                // Handle HTTP errors
                if (error instanceof HttpErrorResponse) {
                    const isProfileEndpoint = req.url.includes('api/profile/');

                    if (error.status === 401 || (error.status === 403 && isTokenExpirationError(error) && !isProfileEndpoint)) {
                        // Only handle 401/403 if we're NOT already on sign-in page
                        // Being on sign-in means either logging in or already logged out
                        const hadSession = hasStoredSession();
                        if (shouldRedirectToSignIn(router, authService)) {
                            clearStoredTokens();
                            if (hadSession) {
                                authAlertService.showSessionExpiredAlert();
                            }
                            router.navigate(['/sign-in']);
                        }
                    }

                    if (error.status === 0) {
                        console.error('Network/CORS error - Backend might be unreachable');
                    }
                }

                return throwError(() => error);
            })
        );
    }
    return next(req);
};